/**
 * Typed Hono RPC Client
 * Provides end-to-end type-safe API calls from client to server
 */

import { hc } from 'hono/client';
import type { AppType } from '@/api';
import type { TrackingInfo, OrderData } from '@/api/schemas';

const client = hc<AppType>('/');

/**
 * Fetch tracking info for a tracking number
 */
export async function fetchTracking(trackingNumber: string): Promise<TrackingInfo | null> {
  const res = await client.api.tracking[':trackingNumber'].$get({
    param: { trackingNumber },
  });

  const body = await res.json() as { data?: TrackingInfo | null; error?: string };

  if (!res.ok) {
    throw new Error(body.error || 'Failed to fetch tracking info');
  }

  return body.data ?? null;
}

/**
 * Verify order ownership (email + order number)
 */
export async function verifyOrderLookup(
  email: string,
  orderNumber: string
): Promise<{ success: boolean; orderNumber: string }> {
  const res = await client.api.lookup.verify.$post({
    json: { email, orderNumber },
  });

  const body = await res.json() as { data?: { success: boolean; orderNumber: string }; error?: string };

  if (!res.ok) {
    throw new Error(body.error || 'Failed to verify order');
  }

  return body.data!;
}

/**
 * Fetch order data (requires prior verification)
 */
export async function fetchOrderData(orderNumber: string): Promise<OrderData | null> {
  const res = await client.api.lookup.order[':orderNumber'].$get({
    param: { orderNumber },
  });

  const body = await res.json() as { data?: OrderData | null; error?: string };

  if (!res.ok) {
    throw new Error(body.error || 'Failed to fetch order data');
  }

  return body.data ?? null;
}

/**
 * Clear the order session (logout)
 */
export async function clearOrderSession(): Promise<void> {
  await client.api.lookup['clear-session'].$post();
}
